import apiClient from './apiClient';
import config from '../utils/config';

/**
 * Service for knowledge base-related API calls
 * The Pinecone index is now taken from process.env.PINECONE_INDEX
 * and the namespace is the projectId
 */
const knowledgeBaseService = {
  /**
   * Configure the backend to use 1024-dimensional embeddings
   * @param {string} projectId - Optional project ID to initialize Pinecone for a specific project
   * @returns {Promise} Promise resolving to the configuration status
   */
  use1024DimensionEmbeddings: async (projectId = null) => {
    const url = projectId 
      ? `/pinecone/projects/${projectId}/use-1024d-index`
      : '/pinecone/use-1024d-index';
    
    const response = await apiClient.post(url);
    return response.data;
  },

  /**
   * Get all documents in the knowledge base
   * @param {string} projectId - Project ID
   * @returns {Promise} Promise resolving to the list of documents
   */
  getDocuments: async (projectId) => {
    const response = await apiClient.get(
      config.PROJECT_ENDPOINTS.KNOWLEDGE_BASE.DOCUMENTS.ALL(projectId)
    );
    return response.data.documents || [];
  },

  /**
   * Get a single document
   * @param {string} projectId - Project ID
   * @param {string} documentId - Document ID
   * @returns {Promise} Promise resolving to the document
   */
  getDocument: async (projectId, documentId) => {
    const response = await apiClient.get(
      config.PROJECT_ENDPOINTS.KNOWLEDGE_BASE.DOCUMENTS.DETAILS(projectId, documentId)
    );
    return response.data.document;
  },

  /**
   * Create a new document
   * @param {string} projectId - Project ID
   * @param {Object} data - Document data
   * @returns {Promise} Promise resolving to the created document
   */
  createDocument: async (projectId, data) => {
    // First set the backend to use 1024-dimensional embeddings
    try {
      await knowledgeBaseService.use1024DimensionEmbeddings(projectId);
    } catch (error) {
      console.warn('Failed to set 1024-dimensional embeddings, using default:', error);
    }
    
    const response = await apiClient.post(
      config.PROJECT_ENDPOINTS.KNOWLEDGE_BASE.DOCUMENTS.ALL(projectId),
      data
    );
    return response.data.document;
  },

  /**
   * Update a document
   * @param {string} projectId - Project ID
   * @param {string} documentId - Document ID
   * @param {Object} data - Updated document data
   * @returns {Promise} Promise resolving to the updated document
   */
  updateDocument: async (projectId, documentId, data) => {
    // First set the backend to use 1024-dimensional embeddings
    try {
      await knowledgeBaseService.use1024DimensionEmbeddings(projectId);
    } catch (error) {
      console.warn('Failed to set 1024-dimensional embeddings, using default:', error);
    }
    
    const response = await apiClient.patch(
      config.PROJECT_ENDPOINTS.KNOWLEDGE_BASE.DOCUMENTS.DETAILS(projectId, documentId),
      data
    );
    return response.data.document;
  },

  /**
   * Delete a document
   * @param {string} projectId - Project ID
   * @param {string} documentId - Document ID
   * @returns {Promise} Promise resolving to the deletion status
   */
  deleteDocument: async (projectId, documentId) => {
    const response = await apiClient.delete(
      config.PROJECT_ENDPOINTS.KNOWLEDGE_BASE.DOCUMENTS.DETAILS(projectId, documentId)
    );
    return response.data;
  },

  /**
   * Search the knowledge base
   * @param {string} projectId - Project ID
   * @param {string} query - Search query
   * @returns {Promise} Promise resolving to the search results
   */
  search: async (projectId, query) => {
    const response = await apiClient.get(
      config.PROJECT_ENDPOINTS.KNOWLEDGE_BASE.SEARCH(projectId),
      { params: { query } }
    );
    return response.data.results;
  },

  /**
   * Upload a file to be converted into a document
   * @param {string} projectId - Project ID
   * @param {FormData} formData - Form data containing the file and metadata
   * @param {function} onProgressUpdate - Callback function for progress updates
   * @returns {Promise} Promise resolving to the created document
   */
  uploadFile: async (projectId, formData, onProgressUpdate) => {
    // First set the backend to use 1024-dimensional embeddings
    try {
      await knowledgeBaseService.use1024DimensionEmbeddings(projectId);
    } catch (error) {
      console.warn('Failed to set 1024-dimensional embeddings, using default:', error);
    }
    
    const response = await apiClient.post(
      config.PROJECT_ENDPOINTS.KNOWLEDGE_BASE.DOCUMENTS.UPLOAD(projectId),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          // Calculate upload progress percentage
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          
          // Call the progress update callback if provided
          if (typeof onProgressUpdate === 'function') {
            onProgressUpdate(percentCompleted);
          }
          
          console.log(`Upload progress: ${percentCompleted}%`);
        }
      }
    );
    return response.data.document;
  },

  /**
   * Add text as a document
   * @param {string} projectId - Project ID
   * @param {Object} data - Document data including title, content, and tags
   * @returns {Promise} Promise resolving to the created document
   */
  addTextDocument: async (projectId, data) => {
    // First set the backend to use 1024-dimensional embeddings
    try {
      await knowledgeBaseService.use1024DimensionEmbeddings(projectId);
    } catch (error) {
      console.warn('Failed to set 1024-dimensional embeddings, using default:', error);
    }
    
    const response = await apiClient.post(
      config.PROJECT_ENDPOINTS.KNOWLEDGE_BASE.DOCUMENTS.TEXT(projectId),
      data
    );
    return response.data.document;
  },

  /**
   * Scrape a website and convert it into a document
   * @param {string} projectId - Project ID
   * @param {Object} data - Data containing the URL, title, and tags
   * @returns {Promise} Promise resolving to the created document
   */
  scrapeWebsite: async (projectId, data) => {
    // First set the backend to use 1024-dimensional embeddings
    try {
      await knowledgeBaseService.use1024DimensionEmbeddings(projectId);
    } catch (error) {
      console.warn('Failed to set 1024-dimensional embeddings, using default:', error);
    }
    
    const response = await apiClient.post(
      config.PROJECT_ENDPOINTS.KNOWLEDGE_BASE.DOCUMENTS.SCRAPE(projectId),
      data
    );
    return response.data.document;
  },

  getProject: async (projectId) => {
    const response = await apiClient.get(`/projects/${projectId}?includePineconeKey=true`);
    return response.data.project;
  },

  updatePineconeConfig: async (projectId, config) => {
    try {
      const response = await apiClient.put(`/projects/${projectId}/pinecone-config`, config);
      return response.data;
    } catch (error) {
      // Handle specific error cases
      if (error.response) {
        const { status, data } = error.response;
        
        if (status === 400) {
          // Handle validation errors
          if (data.missingFields) {
            throw new Error(`Missing required fields: ${Object.entries(data.missingFields)
              .filter(([_, missing]) => missing)
              .map(([field]) => field)
              .join(', ')}`);
          }
          throw new Error(data.message || 'Invalid configuration data');
        }
        
        if (status === 404) {
          throw new Error('Project not found or you do not have permission to access it');
        }
        
        if (status === 500) {
          throw new Error('Server error while updating configuration');
        }
      }
      
      // Handle network errors
      if (error.request) {
        throw new Error('Network error: Could not connect to the server');
      }
      
      // Handle other errors
      throw new Error('An unexpected error occurred');
    }
  },

  /**
   * Test an API endpoint to see its response structure
   * @param {string} projectId - Project ID
   * @param {Object} apiConfig - API configuration including URL, method, headers, and body
   * @returns {Promise} Promise resolving to the API response
   */
  testApiEndpoint: async (projectId, apiConfig) => {
    const response = await apiClient.post(
      config.PROJECT_ENDPOINTS.KNOWLEDGE_BASE.DOCUMENTS.TEST_API(projectId),
      apiConfig
    );
    return response.data.response;
  },

  /**
   * Scrape an API and convert selected fields into a document
   * @param {string} projectId - Project ID
   * @param {Object} data - Data containing API config, selected fields, field mappings, title, and tags
   * @returns {Promise} Promise resolving to the created document
   */
  scrapeApi: async (projectId, data) => {
    // First set the backend to use 1024-dimensional embeddings
    try {
      await knowledgeBaseService.use1024DimensionEmbeddings(projectId);
    } catch (error) {
      console.warn('Failed to set 1024-dimensional embeddings, using default:', error);
    }
    
    const response = await apiClient.post(
      config.PROJECT_ENDPOINTS.KNOWLEDGE_BASE.DOCUMENTS.SCRAPE_API(projectId),
      data
    );
    return response.data.document;
  },

  /**
   * Re-scrape an existing API document with fresh data
   * @param {string} projectId - Project ID
   * @param {string} documentId - Document ID to update
   * @param {Object} data - Data containing API config and template/field configuration
   * @returns {Promise} Promise resolving to the updated document
   */
  rescrapeApi: async (projectId, documentId, data) => {
    // First set the backend to use 1024-dimensional embeddings
    try {
      await knowledgeBaseService.use1024DimensionEmbeddings(projectId);
    } catch (error) {
      console.warn('Failed to set 1024-dimensional embeddings, using default:', error);
    }
    
    const response = await apiClient.post(
      config.PROJECT_ENDPOINTS.KNOWLEDGE_BASE.DOCUMENTS.RESCRAPE_API(projectId, documentId),
      data
    );
    return response.data.document;
  },

  /**
   * Preview template rendering with API response data using Flow Builder syntax
   * @param {string} template - Template string with Flow Builder syntax
   * @param {Object} data - API response data to use for template
   * @returns {string} Rendered template preview
   */
  previewTemplate: (template, data) => {
    if (!template || !data) return '';

    try {
      console.log('[previewTemplate] Processing template:', template.substring(0, 200));
      console.log('[previewTemplate] Data structure:', Object.keys(data));
      
      // Use Flow Builder syntax for template processing
      let result = template;
      
      // Handle array patterns like {{arrayName[*]|template}}
      const arrayPatternMatches = result.match(/\{\{([^}]+)\[\*\]\|([^}]*)\}\}/g);
      if (arrayPatternMatches) {
        console.log('[previewTemplate] Found array patterns:', arrayPatternMatches);
        arrayPatternMatches.forEach(match => {
          const [, arrayPath, itemTemplate] = match.match(/\{\{([^}]+)\[\*\]\|([^}]*)\}\}/);
          console.log('[previewTemplate] Processing array:', arrayPath.trim(), 'with template:', itemTemplate);
          const arrayData = knowledgeBaseService.getNestedValue(data, arrayPath.trim());
          
          if (Array.isArray(arrayData)) {
            console.log('[previewTemplate] Array found with', arrayData.length, 'items');
            const formattedItems = arrayData.slice(0, 3).map((item, index) => { // Limit preview to 3 items
              const formatted = knowledgeBaseService.formatItemTemplate(itemTemplate, item, index);
              console.log(`[previewTemplate] Formatted item ${index + 1}:`, formatted);
              return formatted;
            });
            const previewResult = formattedItems.join('');
            if (arrayData.length > 3) {
              result = result.replace(match, previewResult + `\n... (${arrayData.length - 3} more items)`);
            } else {
              result = result.replace(match, previewResult);
            }
          } else {
            console.log('[previewTemplate] No array found at path:', arrayPath);
            result = result.replace(match, `[No array found at path: ${arrayPath}]`);
          }
        });
      }
      
      // Handle single field placeholders {{field.path}}
      const fieldMatches = result.match(/\{\{([^}]+)\}\}/g);
      if (fieldMatches) {
        fieldMatches.forEach(match => {
          const fieldPath = match.slice(2, -2);
          
          // Skip if it's an array pattern that was already processed
          if (fieldPath.includes('[*]|')) return;
          
          const value = knowledgeBaseService.getNestedValue(data, fieldPath);
          result = result.replace(match, value !== null && value !== undefined ? String(value) : `[${fieldPath} not found]`);
        });
      }
      
      console.log('[previewTemplate] Final result:', result.substring(0, 300));
      return result;
    } catch (error) {
      console.error('[previewTemplate] Error:', error);
      return `Error processing template: ${error.message}`;
    }
  },

  /**
   * Format a single item using Flow Builder template syntax
   * @param {string} template - Template for formatting the item
   * @param {Object} item - Item data
   * @param {number} index - Index of the item
   * @returns {string} Formatted item
   */
  formatItemTemplate: (template, item, index) => {
    if (!template) return '';

    let result = template;

    // Replace index placeholders
    result = result.replace(/\{#\}/g, String(index + 1));  // 1-based numbering
    result = result.replace(/\{##\}/g, String(index));     // 0-based numbering

    // Handle conditional statements {[if:field:operator:value:then:else]}
    const conditionalMatches = result.match(/\{\[if:([^:]+):([^:]+):([^:]*):([^:]*):?([^}]*)\]\}/g);
    if (conditionalMatches) {
      conditionalMatches.forEach(match => {
        const parts = match.slice(4, -2).split(':'); // Remove {[if: and ]}
        if (parts.length >= 4) {
          const [field, operator, value, thenText, elseText = ''] = parts;
          const fieldValue = knowledgeBaseService.getNestedValue(item, field.trim());
          
          let conditionMet = false;
          switch (operator.trim()) {
            case 'equals':
              conditionMet = String(fieldValue) === value.trim();
              break;
            case 'not_equals':
              conditionMet = String(fieldValue) !== value.trim();
              break;
            case 'exists':
              conditionMet = fieldValue !== null && fieldValue !== undefined;
              break;
            case 'empty':
              conditionMet = !fieldValue || fieldValue === '';
              break;
            case 'true':
              conditionMet = fieldValue === true || String(fieldValue).toLowerCase() === 'true';
              break;
            case 'false':
              conditionMet = fieldValue === false || String(fieldValue).toLowerCase() === 'false';
              break;
          }
          
          result = result.replace(match, conditionMet ? thenText.trim() : elseText.trim());
        }
      });
    }

    // Handle date formatting {[field|date:format]}
    const dateFormatMatches = result.match(/\{\[([^|]+)\|date:([^}]+)\]\}/g);
    if (dateFormatMatches) {
      dateFormatMatches.forEach(match => {
        const [, fieldPath, dateFormat] = match.match(/\{\[([^|]+)\|date:([^}]+)\]\}/);
        const value = knowledgeBaseService.getNestedValue(item, fieldPath.trim());
        
        if (value) {
          // Simple date formatting (could be enhanced)
          try {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              let formattedDate = date.toISOString();
              switch (dateFormat.trim()) {
                case 'readable':
                  formattedDate = date.toLocaleDateString();
                  break;
                case 'time':
                  formattedDate = date.toLocaleTimeString();
                  break;
                case 'date':
                  formattedDate = date.toLocaleDateString();
                  break;
              }
              result = result.replace(match, formattedDate);
            } else {
              result = result.replace(match, String(value));
            }
          } catch (e) {
            result = result.replace(match, String(value));
          }
        } else {
          result = result.replace(match, '');
        }
      });
    }

    // Handle regular field placeholders {field.path}
    const fieldMatches = result.match(/\{([^}]+)\}/g);
    if (fieldMatches) {
      fieldMatches.forEach(match => {
        const fieldPath = match.slice(1, -1); // Remove { and }
        
        // Skip if it's already processed (index placeholders)
        if (fieldPath === '#' || fieldPath === '##') return;
        
        const value = knowledgeBaseService.getNestedValue(item, fieldPath);
        if (value !== null && value !== undefined) {
          result = result.replace(match, String(value));
        } else {
          result = result.replace(match, '');
        }
      });
    }

    // Convert newlines to line breaks for preview
    result = result.replace(/\\n/g, '\n');

    return result;
  },

  /**
   * Get nested value from object using dot notation
   * @param {Object} obj - Object to traverse
   * @param {string} path - Dot notation path
   * @returns {*} Value at path or null if not found
   */
  getNestedValue: (obj, path) => {
    if (!obj || !path) return null;
    
    return path.split('.').reduce((current, key) => {
      if (current === null || current === undefined) return null;
      
      // Handle array length
      if (key === 'length' && Array.isArray(current)) {
        return current.length;
      }
      
      return current[key];
    }, obj);
  },
};

export default knowledgeBaseService; 